import React, { lazy } from 'react';

/**
 * Enhanced lazy import with automatic retry and chunk-mismatch recovery.
 * Handles Vite dynamic import errors when new builds/deployments occur.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error: any) {
      console.warn('Dynamic component import failed on first attempt:', error);
      
      const isChunkError =
        error?.message?.includes('dynamically imported module') ||
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('error loading dynamically imported module') ||
        error?.name === 'TypeError';

      const lastReloadTimestamp = parseInt(sessionStorage.getItem('chunk_load_reload_ts') || '0', 10);
      const timeSinceLastReload = Date.now() - lastReloadTimestamp;

      // If chunk mismatch occurs and we haven't reloaded in the last 15 seconds, reload cleanly
      if (isChunkError && timeSinceLastReload > 15000) {
        sessionStorage.setItem('chunk_load_reload_ts', Date.now().toString());
        window.location.reload();
        return new Promise(() => {}); // Hold suspense until reload completes
      }

      // If already reloaded recently, attempt one more fetch after delay
      await new Promise((resolve) => setTimeout(resolve, 300));
      return await componentImport();
    }
  });
}
